// src/pages/VulnerabilityScanDetails.jsx - Detailed Scan View with AI Analysis
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Grid, Alert, CircularProgress,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tabs, Tab, Divider, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Psychology as AnalyzeIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Compare as CompareIcon,
  Block as IgnoreIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import VoiceInput from '../components/VoiceInput';
import { vulnerabilityApi } from '../services/api';
import { toast } from 'react-toastify';

const VulnerabilityScanDetails = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [cisoSummary, setCisoSummary] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [severityFilter, setSeverityFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [includeIgnored, setIncludeIgnored] = useState(false);
  const [ignoreDialog, setIgnoreDialog] = useState({ open: false, finding: null });
  const [ignoreReason, setIgnoreReason] = useState('');
  const ignoreReasonRef = useRef(null);
  const cisoSummaryRef = useRef(null);

  useEffect(() => {
    loadScan();
    loadFindings();
  }, [scanId, severityFilter, includeIgnored]);

  const loadScan = async () => {
    try {
      setLoading(true);
      const response = await vulnerabilityApi.getScan(scanId);
      setScan(response.data);
      if (response.data.analysis) {
        setCisoSummary(response.data.analysis.ciso_summary || '');
      }
    } catch (error) {
      toast.error('Failed to load scan');
      navigate('/vulnerability-scans');
    } finally {
      setLoading(false);
    }
  };

  const loadFindings = async () => {
    try {
      const params = {
        severity: severityFilter || undefined,
        include_ignored: includeIgnored
      };
      const response = await vulnerabilityApi.getFindings(scanId, params);
      setFindings(response.data);
    } catch (error) {
      console.error('Failed to load findings:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!window.confirm('This will use the Claude API to analyze this scan. Continue?')) return;

    try {
      setAnalyzing(true);
      const response = await vulnerabilityApi.analyzeScan(scanId);
      toast.success('Analysis completed successfully!');
      setCisoSummary(response.data.analysis.ciso_summary);
      loadScan();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveSummary = async () => {
    try {
      await vulnerabilityApi.updateCisoSummary(scanId, cisoSummary);
      toast.success('CISO summary updated');
      setEditingSummary(false);
      loadScan();
    } catch (error) {
      toast.error('Failed to update summary');
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      const response = await vulnerabilityApi.generateReport(scanId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vulnerability_Report_${scan.scan_name}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report generated successfully!');
      loadScan();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleIgnoreFinding = (finding) => {
    setIgnoreDialog({ open: true, finding });
    setIgnoreReason('');
  };

  const handleIgnoreSubmit = async () => {
    if (!ignoreReason.trim()) {
      toast.error('Please provide a reason for ignoring this vulnerability');
      return;
    }

    try {
      await vulnerabilityApi.ignoreFinding(ignoreDialog.finding.id, ignoreReason);
      toast.success('Vulnerability ignored');
      setIgnoreDialog({ open: false, finding: null });
      loadFindings();
    } catch (error) {
      toast.error('Failed to ignore vulnerability');
    }
  };

  const handleUnignore = async (findingId) => {
    if (!window.confirm('Remove ignore status from this vulnerability?')) return;

    try {
      await vulnerabilityApi.unignoreFinding(findingId);
      toast.success('Vulnerability un-ignored');
      loadFindings();
    } catch (error) {
      toast.error('Failed to un-ignore vulnerability');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: '#e74c3c',
      High: '#e67e22',
      Medium: '#f39c12',
      Low: '#3498db',
      Info: '#95a5a6'
    };
    return colors[severity] || '#95a5a6';
  };

  const sortedFindings = useMemo(() => {
    if (sortBy === 'device') {
      return [...findings].sort((a, b) => {
        const deviceA = (a.hostname || a.ip_address || '').toLowerCase();
        const deviceB = (b.hostname || b.ip_address || '').toLowerCase();
        return deviceA.localeCompare(deviceB);
      });
    }
    return findings;
  }, [findings, sortBy]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!scan) return null;

  const analysis = scan.analysis || {};
  const keyFindings = analysis.key_findings ? JSON.parse(analysis.key_findings) : [];
  const riskByDevice = analysis.risk_by_device ? JSON.parse(analysis.risk_by_device) : [];
  const recommendations = analysis.remediation_recommendations ? JSON.parse(analysis.remediation_recommendations) : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/vulnerability-scans')}
          sx={{ mb: 2, color: '#6B3FA0' }}
        >
          Back to Scans
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              {scan.scan_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scan.client_name} • {new Date(scan.scan_date).toLocaleDateString()}
            </Typography>
          </div>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {scan.status === 'processing' && (
              <Button
                variant="contained"
                startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <AnalyzeIcon />}
                onClick={handleAnalyze}
                disabled={analyzing}
                sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#5A2F8A' } }}
              >
                {analyzing ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            )}
            {scan.status === 'analyzed' || scan.status === 'report_generated' ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CompareIcon />}
                  onClick={() => navigate(`/vulnerability-scans/${scanId}/compare`)}
                  sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
                >
                  Compare
                </Button>
                <Button
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleGenerateReport}
                  disabled={generating}
                  sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#229954' } }}
                >
                  {generating ? 'Generating...' : 'Generate Report'}
                </Button>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#ecf0f1' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                {scan.total_vulnerabilities}
              </Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#fadbd8' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e74c3c' }}>
                {scan.critical_count}
              </Typography>
              <Typography variant="body2">Critical</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#fdebd0' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#e67e22' }}>
                {scan.high_count}
              </Typography>
              <Typography variant="body2">High</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#fcf3cf' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f39c12' }}>
                {scan.medium_count}
              </Typography>
              <Typography variant="body2">Medium</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 2.4 }}>
          <Card sx={{ textAlign: 'center', bgcolor: '#d4efdf' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: scan.overall_score >= 70 ? '#e74c3c' : scan.overall_score >= 40 ? '#f39c12' : '#27ae60' }}>
                {scan.overall_score}/100
              </Typography>
              <Typography variant="body2">Risk Score</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Findings" />
          <Tab label="Analysis" disabled={!analysis.ciso_summary} />
        </Tabs>
      </Card>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {/* CISO Summary */}
          {analysis.ciso_summary && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      CISO Executive Summary
                      {Boolean(analysis.ciso_summary_edited) && (
                        <Chip label="Edited" size="small" color="primary" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {editingSummary ? (
                      <Button startIcon={<SaveIcon />} onClick={handleSaveSummary} variant="contained" size="small">
                        Save
                      </Button>
                    ) : (
                      <Button startIcon={<EditIcon />} onClick={() => setEditingSummary(true)} size="small">
                        Edit
                      </Button>
                    )}
                  </Box>
                  {editingSummary ? (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={14}
                        value={cisoSummary}
                        onChange={(e) => setCisoSummary(e.target.value)}
                        inputRef={cisoSummaryRef}
                      />
                      <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                        <VoiceInput
                          onTranscript={(text) => setCisoSummary(text)}
                          currentValue={cisoSummary}
                          inputRef={cisoSummaryRef}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{cisoSummary}</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Key Findings */}
          {keyFindings.length > 0 && (
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Key Findings</Typography>
                  {keyFindings.map((finding, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {idx + 1}. {finding.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>{finding.description}</Typography>
                      <Typography variant="body2" color="text.secondary">Impact: {finding.impact}</Typography>
                      {idx < keyFindings.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Findings Tab */}
      {currentTab === 1 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Severity</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  label="Filter by Severity"
                >
                  <MenuItem value="">All Severities</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Info">Info</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort by"
                >
                  <MenuItem value="default">Severity (default)</MenuItem>
                  <MenuItem value="device">Device</MenuItem>
                </Select>
              </FormControl>
              <Button
                startIcon={includeIgnored ? <HideIcon /> : <ViewIcon />}
                onClick={() => setIncludeIgnored(!includeIgnored)}
                variant="outlined"
              >
                {includeIgnored ? 'Hide' : 'Show'} Ignored
              </Button>
            </CardContent>
          </Card>

          {/* Findings Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#ecf0f1' }}>
                <TableRow>
                  <TableCell><strong>Severity</strong></TableCell>
                  <TableCell><strong>Vulnerability</strong></TableCell>
                  <TableCell><strong>Device</strong></TableCell>
                  <TableCell><strong>CVSS</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFindings.map((finding, idx) => {
                  const device = finding.hostname || finding.ip_address || 'Unknown';
                  const prevDevice = idx > 0
                    ? (sortedFindings[idx - 1].hostname || sortedFindings[idx - 1].ip_address || 'Unknown')
                    : null;
                  const showDeviceHeader = sortBy === 'device' && device !== prevDevice;

                  return (
                    <React.Fragment key={finding.id}>
                      {showDeviceHeader && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{
                              bgcolor: '#4a1e6b',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              py: 0.75,
                              letterSpacing: 0.5
                            }}
                          >
                            {device}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ opacity: finding.is_ignored ? 0.5 : 1 }}>
                        <TableCell>
                          <Chip
                            label={finding.severity}
                            size="small"
                            sx={{ bgcolor: getSeverityColor(finding.severity), color: 'white', fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{finding.nvt_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {finding.summary?.substring(0, 100)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{device}</Typography>
                        </TableCell>
                        <TableCell>{finding.cvss_score || 'N/A'}</TableCell>
                        <TableCell>
                          {finding.is_ignored ? (
                            <Chip label="Ignored" size="small" color="default" />
                          ) : (
                            <Chip label="Active" size="small" color="success" />
                          )}
                        </TableCell>
                        <TableCell>
                          {finding.is_ignored ? (
                            <IconButton size="small" onClick={() => handleUnignore(finding.id)} title="Un-ignore">
                              <ViewIcon />
                            </IconButton>
                          ) : (
                            <IconButton size="small" onClick={() => handleIgnoreFinding(finding)} title="Ignore">
                              <IgnoreIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Analysis Tab */}
      {currentTab === 2 && (
        <Grid container spacing={3}>
          {/* Risk by Device */}
          {riskByDevice.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Risk by Device</Typography>
                  {riskByDevice.slice(0, 10).map((device, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{device.device}</Typography>
                      <Typography variant="caption">
                        Risk: {device.risk_level} • Vulnerabilities: {device.vulnerability_count}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Remediation Recommendations */}
          {recommendations.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Remediation Recommendations
                  </Typography>
                  {recommendations.map((rec, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {rec.priority}. {rec.action}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{rec.rationale}</Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Chip label={`${rec.affected_systems} systems`} size="small" />
                        <Chip label={rec.estimated_effort} size="small" color="primary" />
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* Ignore Dialog */}
      <Dialog open={ignoreDialog.open} onClose={() => setIgnoreDialog({ open: false, finding: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Ignore Vulnerability</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This vulnerability will be marked as ignored for this report only. If it appears in future scans, it will need to be ignored again.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Ignoring (Required)"
            value={ignoreReason}
            onChange={(e) => setIgnoreReason(e.target.value)}
            placeholder="e.g., False positive, accepted risk, compensating controls in place..."
            inputRef={ignoreReasonRef}
            InputProps={{
              endAdornment: (
                <VoiceInput
                  onTranscript={(text) => setIgnoreReason(text)}
                  currentValue={ignoreReason}
                  inputRef={ignoreReasonRef}
                />
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreDialog({ open: false, finding: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleIgnoreSubmit} disabled={!ignoreReason.trim()}>
            Ignore
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VulnerabilityScanDetails;
